import {
  BarChart3,
  Building2,
  Check,
  ChevronsUpDown,
  FilePlus2,
  FileText,
  Keyboard,
  LayoutDashboard,
  LayoutTemplate,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Moon,
  Package,
  Plus,
  Repeat,
  Search,
  Settings,
  Sun,
  UserPlus,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useNavigation } from 'react-router';
import { toast } from 'sonner';
import { BrandLogo } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { Avatar, Kbd } from '@/components/ui/display';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Tooltip,
} from '@/components/ui/menu';
import { setAuthState, useAuth } from '@/hooks/auth';
import { useBusiness } from '@/hooks/business';
import { api } from '@/lib/api';
import { queryClient } from '@/lib/query';
import { adoptAccountTheme, chooseTheme, useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { useWorkspace } from '@/lib/workspace';
import { CommandPalette, usePalette } from './command-palette';

const NAV_GROUPS = [
  { label: null, items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }] },
  {
    label: 'Billing',
    items: [
      { to: '/invoices', label: 'Invoices', icon: FileText },
      { to: '/recurring', label: 'Recurring', icon: Repeat },
      { to: '/payments', label: 'Payments', icon: Wallet },
    ],
  },
  {
    label: 'Customers & catalog',
    items: [
      { to: '/customers', label: 'Customers', icon: Users },
      { to: '/products', label: 'Products', icon: Package },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { to: '/templates', label: 'Templates', icon: LayoutTemplate },
      { to: '/reports', label: 'Reports', icon: BarChart3 },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
] as const;

function NavItem({
  to,
  label,
  icon: Icon,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: typeof FileText;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group relative flex h-9 items-center gap-3 rounded-lg px-3 text-[13.5px] font-medium transition-colors',
          isActive
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-200'
            : 'text-ink-muted hover:bg-subtle hover:text-ink',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive ? (
            <span
              className="absolute inset-y-2 left-0 w-[3px] rounded-r-full bg-brand-600 dark:bg-brand-400"
              aria-hidden
            />
          ) : null}
          <Icon
            className={cn(
              'size-[18px] shrink-0',
              isActive ? 'text-brand-600 dark:text-brand-300' : 'text-ink-faint group-hover:text-ink-muted',
            )}
          />
          {label}
        </>
      )}
    </NavLink>
  );
}

export function useSignOut() {
  const navigate = useNavigate();
  return async () => {
    try {
      await api('/api/auth/signout', { method: 'POST' });
    } catch {
      // Sign out locally even if the request fails.
    }
    setAuthState(null);
    queryClient.removeQueries({ queryKey: ['b'] });
    navigate('/signin', { replace: true });
    toast.success('You have been signed out.');
  };
}

function BusinessSwitcher() {
  const auth = useAuth();
  const business = useBusiness();
  const setBusiness = useWorkspace((s) => s.setBusiness);
  const navigate = useNavigate();
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-xl border border-line bg-subtle/60 px-2.5 py-2 text-left transition-colors hover:border-line-strong hover:bg-subtle"
        >
          <Avatar name={business.name} src={business.logo?.url} size="sm" className="rounded-lg" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold text-ink">{business.name}</span>
            <span className="block truncate text-[11.5px] capitalize text-ink-faint">
              {business.role} · {business.currency}
            </span>
          </span>
          <ChevronsUpDown className="size-4 text-ink-faint" />
        </button>
      </MenuTrigger>
      <MenuContent align="start" className="w-[248px]">
        <MenuLabel>Businesses</MenuLabel>
        {auth.businesses.map((b) => (
          <MenuItem
            key={b.id}
            icon={<Avatar name={b.name} src={b.logoUrl} size="xs" className="rounded-md" />}
            onSelect={() => {
              if (b.id === business.id) return;
              setBusiness(b.id);
              navigate('/dashboard');
              toast.success(`Switched to ${b.name}`);
            }}
          >
            <span className="flex items-center justify-between gap-2">
              <span className="truncate">{b.name}</span>
              {b.id === business.id ? <Check className="size-4 text-brand-600 dark:text-brand-400" /> : null}
            </span>
          </MenuItem>
        ))}
        <MenuSeparator />
        <MenuItem icon={<Plus />} onSelect={() => navigate('/onboarding?new=1')}>
          Add another business
        </MenuItem>
        <MenuItem icon={<Building2 />} onSelect={() => navigate('/settings/business')}>
          Business settings
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

function ThemeMenuItems() {
  const theme = useTheme((s) => s.theme);
  const options = [
    { value: 'light', label: 'Light', icon: <Sun /> },
    { value: 'dark', label: 'Dark', icon: <Moon /> },
    { value: 'system', label: 'System', icon: <Monitor /> },
  ] as const;
  return (
    <>
      <MenuLabel>Appearance</MenuLabel>
      {options.map((option) => (
        <MenuItem key={option.value} icon={option.icon} onSelect={() => void chooseTheme(option.value)}>
          <span className="flex items-center justify-between">
            {option.label}
            {theme === option.value ? <Check className="size-4 text-brand-600 dark:text-brand-400" /> : null}
          </span>
        </MenuItem>
      ))}
    </>
  );
}

function UserMenu({ compact = false }: { compact?: boolean }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const signOut = useSignOut();
  return (
    <Menu>
      <MenuTrigger asChild>
        {compact ? (
          <button type="button" className="rounded-full" aria-label="Account menu">
            <Avatar name={auth.user.name} size="sm" />
          </button>
        ) : (
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-subtle"
          >
            <Avatar name={auth.user.name} size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold text-ink">{auth.user.name}</span>
              <span className="block truncate text-[11.5px] text-ink-faint">{auth.user.email}</span>
            </span>
            <ChevronsUpDown className="size-4 text-ink-faint" />
          </button>
        )}
      </MenuTrigger>
      <MenuContent align={compact ? 'end' : 'start'} className="w-[240px]">
        <div className="px-2.5 py-2">
          <div className="truncate text-[13px] font-semibold text-ink">{auth.user.name}</div>
          <div className="truncate text-xs text-ink-faint">{auth.user.email}</div>
        </div>
        <MenuSeparator />
        <MenuItem icon={<UserRound />} onSelect={() => navigate('/settings/account')}>
          Account
        </MenuItem>
        <MenuItem icon={<Settings />} onSelect={() => navigate('/settings')}>
          Settings
        </MenuItem>
        <MenuSeparator />
        <ThemeMenuItems />
        <MenuSeparator />
        <MenuItem icon={<LogOut />} onSelect={signOut}>
          Sign out
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col gap-4 px-3 pb-3 pt-5">
      <div className="px-2">
        <BrandLogo />
      </div>
      <BusinessSwitcher />
      <Button
        className="w-full justify-center"
        onClick={() => {
          onNavigate?.();
          navigate('/invoices/new');
        }}
      >
        <FilePlus2 /> New invoice
      </Button>
      <nav className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto px-1" aria-label="Main">
        {NAV_GROUPS.map((group) => (
          <div key={group.label ?? 'home'} className="flex flex-col gap-0.5">
            {group.label ? (
              <div className="px-3 pb-1.5 pt-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">
                {group.label}
              </div>
            ) : null}
            {group.items.map((item) => (
              <NavItem key={item.to} {...item} onNavigate={onNavigate} />
            ))}
          </div>
        ))}
      </nav>
      <div>
        <div className="mb-2 hidden items-center gap-2 rounded-lg bg-subtle px-3 py-2 text-[11.5px] text-ink-muted lg:flex">
          <Keyboard className="size-3.5 shrink-0 text-ink-faint" aria-hidden />
          <span className="flex items-center gap-1">
            <Kbd>N</Kbd> new invoice <span className="text-ink-faint">·</span> <Kbd>/</Kbd> search
          </span>
        </div>
        <UserMenu />
      </div>
    </div>
  );
}

function TopProgress() {
  const navigation = useNavigation();
  const active = navigation.state !== 'idle';
  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 top-0 z-[80] h-0.5 origin-left bg-brand-500 transition-transform duration-500',
        active ? 'scale-x-75' : 'scale-x-0 opacity-0',
      )}
      aria-hidden
    />
  );
}

function QuickCreate() {
  const navigate = useNavigate();
  return (
    <Menu>
      <MenuTrigger asChild>
        <Button variant="outline" size="sm" className="hidden sm:inline-flex">
          <Plus /> Create
        </Button>
      </MenuTrigger>
      <MenuContent>
        <MenuItem icon={<FilePlus2 />} onSelect={() => navigate('/invoices/new')} shortcut="N">
          Invoice
        </MenuItem>
        <MenuItem icon={<Repeat />} onSelect={() => navigate('/recurring/new')}>
          Recurring invoice
        </MenuItem>
        <MenuItem icon={<UserPlus />} onSelect={() => navigate('/customers?new=1')}>
          Customer
        </MenuItem>
        <MenuItem icon={<Package />} onSelect={() => navigate('/products?new=1')}>
          Product or service
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

function ThemeToggle() {
  const dark = useTheme((s) => s.dark);
  return (
    <Tooltip content={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => void chooseTheme(dark ? 'light' : 'dark')}
        aria-label="Toggle dark mode"
      >
        {dark ? <Sun /> : <Moon />}
      </Button>
    </Tooltip>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const openPalette = usePalette((s) => s.setOpen);
  const navigate = useNavigate();
  const accountTheme = useAuth().user.preferences.theme;

  useEffect(() => adoptAccountTheme(accountTheme), [accountTheme]);
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)))
        return;
      if (event.key === 'n' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        navigate('/invoices/new');
      }
      if (event.key === '/' && !event.metaKey) {
        event.preventDefault();
        openPalette(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, openPalette]);

  return (
    <div className="min-h-dvh bg-canvas">
      <TopProgress />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[90] focus:rounded-lg focus:bg-surface focus:px-4 focus:py-2 focus:shadow-pop"
      >
        Skip to content
      </a>
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[264px] border-r border-line bg-surface lg:block">
        <SidebarContent />
      </aside>

      <RadixDialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <RadixDialog.Portal>
          <RadixDialog.Overlay className="fixed inset-0 z-50 bg-slate-950/40 data-[state=open]:animate-fade-in lg:hidden" />
          <RadixDialog.Content className="fixed inset-y-0 left-0 z-50 w-[284px] border-r border-line bg-surface shadow-pop data-[state=open]:animate-pop-in lg:hidden">
            <RadixDialog.Title className="sr-only">Navigation</RadixDialog.Title>
            <RadixDialog.Description className="sr-only">Main navigation</RadixDialog.Description>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </RadixDialog.Content>
        </RadixDialog.Portal>
      </RadixDialog.Root>

      <div className="lg:pl-[264px]">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-canvas/85 px-4 backdrop-blur-md sm:px-6">
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <MenuIcon />
          </Button>
          <div className="lg:hidden">
            <BrandLogo size="sm" />
          </div>
          <button
            type="button"
            onClick={() => openPalette(true)}
            className="ml-auto flex h-9 w-full max-w-[380px] items-center gap-2.5 rounded-lg border border-line bg-surface px-3 text-[13px] text-ink-faint shadow-sm transition-colors hover:border-line-strong max-sm:w-9 max-sm:justify-center max-sm:px-0 lg:ml-0"
            aria-label="Search"
          >
            <Search className="size-4 shrink-0" />
            <span className="flex-1 text-left max-sm:hidden">Search invoices, customers…</span>
            <span className="flex gap-1 max-sm:hidden">
              <Kbd>⌘</Kbd>
              <Kbd>K</Kbd>
            </span>
          </button>
          <div className="flex items-center gap-1.5 lg:ml-auto">
            <QuickCreate />
            <ThemeToggle />
            <div className="lg:hidden">
              <UserMenu compact />
            </div>
          </div>
        </header>
        <main id="main" className="mx-auto w-full max-w-[1400px] px-4 pb-16 pt-6 sm:px-6 lg:px-8 lg:pt-8">
          {children ?? <Outlet />}
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}
